import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ZagIcon } from '../icon/icon';

/**
 * Data structure for GroupListRow component
 */
export interface GroupListRowData {
  id: string;
  name: string;
  description?: string | null;
  memberCount: number;
  roleCount: number;
}

/**
 * GroupListRow Component
 *
 * Displays a group's information in a horizontal row format for list views.
 * Shows group name, description, and stats (members, roles).
 *
 * @example
 * ```html
 * <zag-group-list-row
 *   [group]="groupData"
 *   [editRoute]="['/settings/groups', groupData.id]"
 *   (rowClick)="onGroupSelected($event)"
 *   (deleteClick)="onDeleteGroup($event)">
 * </zag-group-list-row>
 * ```
 */
@Component({
  selector: 'zag-group-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ZagIcon],
  host: {
    '[class.zag-group-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  template: `
    <span zagIcon="groups" class="zag-group-list-row__icon"></span>

    <div class="zag-group-list-row__info">
      <span class="zag-group-list-row__name">{{ group().name }}</span>
      @if (group().description) {
        <span class="zag-group-list-row__description">{{ group().description }}</span>
      }
    </div>

    <div class="zag-group-list-row__stats">
      <span class="zag-group-list-row__stat">
        <span zagIcon="person"></span>
        {{ group().memberCount }}
      </span>
      <span class="zag-group-list-row__stat">
        <span zagIcon="security"></span>
        {{ group().roleCount }}
      </span>
    </div>

    <div class="zag-group-list-row__actions">
      @if (showDelete()) {
        <button
          type="button"
          class="zag-group-list-row__action zag-group-list-row__action--delete"
          [disabled]="deleteDisabled()"
          [attr.aria-label]="deleteAriaLabel() || null"
          (click)="onDeleteClick($event)"
        >
          <span zagIcon="delete"></span>
        </button>
      }
    </div>
  `,
  styles: [
    `
      .zag-group-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        cursor: pointer;
        transition: box-shadow 0.15s ease;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }
      }

      .zag-group-list-row__icon {
        font-size: 24px;
        color: var(--color-primary);
        flex-shrink: 0;
      }

      .zag-group-list-row__info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .zag-group-list-row__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-group-list-row__description {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-group-list-row__stats {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        flex-shrink: 0;
      }

      .zag-group-list-row__stat {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-sm);
        color: var(--text-color-tertiary);

        [zagIcon] {
          font-size: 16px;
        }
      }

      .zag-group-list-row__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        flex-shrink: 0;
      }

      .zag-group-list-row__action {
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        text-decoration: none;
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover {
          background-color: var(--color-info-light);
          color: var(--color-primary);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;

          &:hover {
            background-color: transparent;
            color: var(--text-color-secondary);
          }
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      .zag-group-list-row__action--delete {
        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }
      }

      @media (max-width: 768px) {
        .zag-group-list-row__stats {
          display: none;
        }
      }
    `,
  ],
})
export class GroupListRow {
  private readonly router = inject(Router);

  /** The group data to display */
  readonly group = input.required<GroupListRowData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Accessible label for the edit action */
  readonly editAriaLabel = input('');

  /** Whether to show the delete button */
  readonly showDelete = input(true);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the row is clicked */
  readonly rowClick = output<GroupListRowData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<GroupListRowData>();

  /** Handle row click - navigates to editRoute if set, then emits rowClick */
  onRowClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.rowClick.emit(this.group());
  }

  /** Handle keyboard activation for accessibility */
  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.group());
  }
}
